/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { FaLock } from "react-icons/fa";
import CheckoutSteps from "../../components/CheckoutSteps";
import { Link, useNavigate } from "react-router-dom";

// Redux
// eslint-disable-next-line no-unused-vars
import { useSelector, useDispatch } from "react-redux";
import {
  useCreateNewOrderMutation,
  useEstimateShippingCostMutation,
} from "../../redux/api/orderApi";
import { clearCart } from "../../redux/features/cartSlice";

import { caluclateOrderCost } from "../../helpers/caluclateOrderCost";

import MetaData from "../../components/layout/MetaData";
import Message from "../../components/Message";
import Loader from "../../components/Loader";
import { toast } from "react-toastify";

const DEFAULT_ITEM_WEIGHT_GRAMS = 500;

const COUNTRY_CODE_MAP = {
  australia: "AU",
  "united states": "US",
  usa: "US",
  canada: "CA",
  china: "CN",
  germany: "DE",
  france: "FR",
  japan: "JP",
  singapore: "SG",
  "united kingdom": "GB",
  uk: "GB",
};

/**
 * 将国家信息标准化为 ISO 两位国家码。
 * @param {string} country 收货国家（名称或国家码）
 * @returns {string} ISO 两位国家码，无法识别时返回空字符串
 */
const normalizeCountryCode = (country) => {
  const rawValue = String(country || "").trim();
  if (!rawValue) {
    return "";
  }

  if (/^[A-Za-z]{2}$/.test(rawValue)) {
    return rawValue.toUpperCase();
  }

  const mappedCode = COUNTRY_CODE_MAP[rawValue.toLowerCase()];
  return mappedCode || "";
};

/**
 * 解析商品重量并转换为克（g）。
 * @param {number|string} weight 商品重量
 * @returns {number|null} 克数，无法解析时返回 null
 */
const parseWeightToGrams = (weight) => {
  if (weight === null || weight === undefined) {
    return null;
  }

  const weightText = String(weight).trim().toLowerCase();
  if (!weightText) {
    return null;
  }

  const parsedWeight = Number.parseFloat(weightText.replace(/[^0-9.]/g, ""));
  if (!Number.isFinite(parsedWeight) || parsedWeight <= 0) {
    return null;
  }

  if (weightText.includes("kg")) {
    return parsedWeight * 1000;
  }

  return parsedWeight;
};

/**
 * 计算购物车总重量（克）。
 * @param {Array} items 购物车商品列表
 * @returns {number} 总重量（克）
 */
const getCartTotalWeightInGrams = (items = []) => {
  return items.reduce((totalWeight, item) => {
    const itemWeight = parseWeightToGrams(item?.weight ?? item?.Weight);
    // TODO: 当前购物车未统一存储重量字段，暂用 500g 作为兜底值，后续可改为真实商品重量。
    const normalizedWeight = Number.isFinite(itemWeight)
      ? itemWeight
      : DEFAULT_ITEM_WEIGHT_GRAMS;
    const quantity = Number(item?.quantity || 0);
    return totalWeight + normalizedWeight * quantity;
  }, 0);
};

/**
 * 从后端预估响应中提取可用运费。
 * @param {Object} response 后端 `/logistics/estimate` 响应
 * @returns {number|null} 运费金额，无法提取时返回 null
 */
const extractEstimatedShippingFee = (response) => {
  const estimateWrapper = response?.estimate || {};
  const quoteList = Array.isArray(estimateWrapper?.quotes)
    ? estimateWrapper.quotes
    : [];
  const rawCostList = Array.isArray(estimateWrapper?.raw?.costList)
    ? estimateWrapper.raw.costList
    : [];

  const candidates = [
    estimateWrapper?.estimate,
    quoteList[0],
    rawCostList[0],
  ].filter(Boolean);

  for (const candidate of candidates) {
    const rawFee =
      candidate?.lumpSumFee ??
      candidate?.lump_sum_fee ??
      candidate?.totalFee ??
      candidate?.total_fee ??
      candidate?.estimatedCost ??
      candidate?.estimated_cost ??
      candidate?.fee ??
      candidate?.cost;
    const parsedFee = Number(rawFee);
    if (Number.isFinite(parsedFee) && parsedFee >= 0) {
      return Number(parsedFee.toFixed(2));
    }
  }

  return null;
};

/**
 * 确认订单页：展示订单信息并提交创建订单。
 */
const ConfirmOrder = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const { cartItems, shippingInfo, paymentMethod } = useSelector(
    (state) => state.cart
  );
  const { user } = useSelector((state) => state.auth);
  const {
    itemsPrice: fallbackItemsPrice,
    shippingPrice: fallbackShippingPrice,
    taxPrice: fallbackTaxPrice,
  } = caluclateOrderCost(cartItems);
  const [estimatedShippingPrice, setEstimatedShippingPrice] = useState(null);
  const [shippingPriceSource, setShippingPriceSource] = useState("fallback");

  const itemsPrice = Number(fallbackItemsPrice).toFixed(2);
  const shippingPrice =
    estimatedShippingPrice === null ? fallbackShippingPrice : estimatedShippingPrice;
  const totalPrice = (
    Number(itemsPrice) +
    Number(shippingPrice) +
    Number(fallbackTaxPrice)
  ).toFixed(2);

  const [createNewOrder, { isLoading, error, isSuccess }] =
    useCreateNewOrderMutation();
  const [estimateShippingCost] = useEstimateShippingCostMutation();

  /**
   * 格式化单价显示为 6 位小数，避免因后端返回类型不一致导致渲染异常。
   * @param {number|string} price 商品单价
   * @returns {string} 固定 6 位小数字符串
   */
  const formatUnitPrice = (price) => Number(price || 0).toFixed(6);

  useEffect(() => {
    if (isSuccess) {
      toast.success("Order Placed Successfully");
    }
    if (error) {
      toast.error(error?.data?.message);
    }
  }, [isSuccess, error]);

  useEffect(() => {
    /**
     * 根据购物车与收货国家调用 4PX 运费预估。
     */
    const estimateOrderShipping = async () => {
      if (!cartItems?.length) {
        setEstimatedShippingPrice(null);
        setShippingPriceSource("fallback");
        return;
      }

      const countryCode = normalizeCountryCode(shippingInfo?.country);
      if (!countryCode) {
        setEstimatedShippingPrice(null);
        setShippingPriceSource("fallback");
        return;
      }

      const totalWeightInGrams = Math.max(
        1,
        Math.round(getCartTotalWeightInGrams(cartItems))
      );

      try {
        const estimateResponse = await estimateShippingCost({
          country_code: countryCode,
          weight: String(totalWeightInGrams),
          cargocode: "P",
        }).unwrap();

        const estimatedFee = extractEstimatedShippingFee(estimateResponse);
        if (Number.isFinite(estimatedFee)) {
          setEstimatedShippingPrice(estimatedFee);
          setShippingPriceSource("4px");
          return;
        }
      } catch (estimateError) {
        console.error("[ConfirmOrder] 4PX 运费预估失败:", estimateError);
      }

      setEstimatedShippingPrice(null);
      setShippingPriceSource("fallback");
    };

    estimateOrderShipping();
  }, [cartItems, shippingInfo?.country, estimateShippingCost]);

  /**
   * 提交订单：创建成功后清空购物车并跳转订单详情页。
   */
  const submitHandler = async () => {
    const {
      itemsPrice: recalculatedItemsPrice,
      shippingPrice: recalculatedShippingPrice,
      taxPrice,
    } = caluclateOrderCost(cartItems);
    const finalShippingPrice =
      estimatedShippingPrice === null
        ? recalculatedShippingPrice
        : estimatedShippingPrice;
    const finalTotalPrice = (
      Number(recalculatedItemsPrice) +
      Number(finalShippingPrice) +
      Number(taxPrice)
    ).toFixed(2);

    // Create WireTransfer Order
    const orderData = {
      orderItems: cartItems,
      shippingInfo,
      paymentMethod: paymentMethod,
      paymentInfo: {
        status: "Not Paid",
      },
      itemsPrice: Number(recalculatedItemsPrice),
      shippingAmount: Number(finalShippingPrice),
      taxAmount: taxPrice,
      totalAmount: Number(finalTotalPrice),
    };
    const res = await createNewOrder(orderData);
    if (res) {
      dispatch(clearCart());
      navigate(`/me/order/${res?.data?.order?._id}`);
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <MetaData title="Confirm Order" />
      <CheckoutSteps shipping payment confirmOrder />
      <div className="flex flex-wrap mx-4">
        {/* Shipping Info */}
        <div className="flex-1">
          <div className="bg-white shadow-md rounded-lg p-4 mb-4">
            <h2 className="text-xl font-semibold mb-3">Shipping</h2>
            <p>
              <strong>Name:</strong> {user?.name}
            </p>
            <p>
              <strong>Phone:</strong> {shippingInfo?.phoneNo}
            </p>
            <p>
              <strong>Address:</strong>{" "}
              {`${shippingInfo?.address}, ${shippingInfo?.city}, ${shippingInfo?.zipCode}, ${shippingInfo?.country}`}
            </p>
          </div>

          <div className="bg-white shadow-md rounded-lg p-4 mb-4">
            <h2 className="text-xl font-semibold mb-3">Payment Method</h2>
            <p>
              <strong>Method:</strong> {paymentMethod}
            </p>
          </div>

          <div className="bg-white shadow-md rounded-lg p-4">
            <h2 className="text-xl font-semibold mb-3">Order Items</h2>
            {cartItems.length === 0 ? (
              <Message>Your cart is empty</Message>
            ) : (
              <div className="divide-y divide-gray-200">
                <h4 className="text-xl font-semibold mt-4">Your Cart Items:</h4>
                {cartItems?.map((item, index) => (
                  <React.Fragment key={index}>
                    <hr />
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <img
                          src={item?.image}
                          alt="Product"
                          className="h-12 w-auto"
                        />
                        <Link
                          to={`/product/${item.product}`}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          {item?.name}
                        </Link>
                      </div>
                      <p>
                        {item?.quantity} x ${formatUnitPrice(item?.price)} ={" "}
                        <strong>
                          ${(item?.quantity * item.price).toFixed(2)}
                        </strong>
                      </p>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Order Summary */}
        <form
          onSubmit={submitHandler}
          className="p-4 rounded-xl shadow-lg h-full "
        >
          <div className="flex items-center space-x-2 p-2 bg-blue-200 rounded">
            <FaLock className="text-blue-800" size={24} />
            <p className="text-blue-800 font-medium">
              Your data will be encrypted and transmitted securely.
            </p>
          </div>
          <div className="flex flex-col justify-between">
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Qty
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Price
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {cartItems.map((item, index) => (
                  <tr key={index}>
                    <td className="px-4 py-4">{item.name}</td>
                    <td className="text-center">{item.quantity}</td>
                    <td className="text-center">${formatUnitPrice(item.price)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan="2" className="px-4 py-2 font-semibold">
                    Subtotal
                  </td>
                  <td className="px-4 py-2 text-right font-semibold">
                    ${itemsPrice}
                  </td>
                </tr>
                <tr>
                  <td colSpan="2" className="px-4 py-2 font-semibold">
                    Shipping Charges
                  </td>
                  <td className="px-4 py-2 text-right font-semibold">
                    ${shippingPrice}
                  </td>
                </tr>
                {shippingPriceSource === "4px" && (
                  <tr>
                    <td colSpan="3" className="px-4 pb-2 text-xs text-gray-500">
                      Estimated by 4PX
                    </td>
                  </tr>
                )}
                <tr>
                  <td colSpan="2" className="px-4 py-2 font-semibold">
                    Grand Total
                  </td>
                  <td className="px-4 py-2 text-right font-semibold">
                    ${totalPrice}
                  </td>
                </tr>
              </tfoot>
            </table>
            <div>
              <button
                className="mt-4 w-full bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                aria-label="Checkout via PayPal"
                type="submit"
              >
                Checkout via PayPal
              </button>
              <button
                type="button"
                onClick={() => setShowModal(true)}
                className="mt-2 w-full bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                aria-label="Checkout via Wire Transfer"
              >
                Checkout via Wire Transfer
              </button>

              {/* Modal Popup */}
              {showModal && (
                <div
                  className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full "
                  id="my-modal"
                >
                  <div className="relative top-20 mx-auto p-4 border w-96 shadow-lg rounded-md bg-white mt-10 h-5/5">
                    <div className="mt-4 text-center">
                      <h5 className="text-lg font-semibold mb-3">
                        Bank Transfer Instructions
                      </h5>
                      <p>
                        You are one step away from completing your order. To
                        complete the order process, transfer the cart amount to
                        the following bank account. Total cart value:{" "}
                        <strong className="text-blue-500">${totalPrice}</strong>
                        .
                      </p>
                      <p className="mb-4">
                        <strong>Beneficiary:</strong> Creatyea Technology (SZ)
                        CO., Limited
                        <br />
                        <strong>Bank:</strong> CHINA EVERBRIGHT BANK SHENZHEN
                        BRANCH
                        <br />
                        <strong>A/C code:</strong> 38911488000024600
                        <br />
                        <strong>Bank Address:</strong> CEB BUILDING NO.18 ZIZHU
                        QI AVENUE, ZHUZILIN SI ROAD, FUTIAN DISTRICT, SHENZHEN,
                        CHINA 518040
                        <br />
                        <strong>SWIFT:</strong> EVERCNBJSZ1
                      </p>
                      <p>
                        Once the payment is made, send the payment confirmation
                        details like date, reference number, screenshot etc to:
                        <a
                          href="mailto:hello@creatyea.com"
                          className="text-blue-500 hover:text-blue-700"
                        >
                          {" "}
                          hello@creatyea.com
                        </a>
                      </p>
                      <button
                        type="submit"
                        className={`mt-4 w-full bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded ${
                          isLoading && "opacity-50"
                        }`}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <i className="fas fa-spinner fa-pulse"></i>
                        ) : (
                          "Confirm Order"
                        )}
                      </button>
                      <button
                        onClick={() => setShowModal(false)}
                        className="mt-2 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default ConfirmOrder;
